import {sendEvent} from '@github-ui/hydro-analytics'
import {featurePreviewTogglePath} from '@github-ui/paths'
import {rememberIssueViewerActivityTimelineChoice} from '@github-ui/preheat-issues-manager/issue-viewer-storage-key'
import {verifiedFetch} from '@github-ui/verified-fetch'
import {useCallback} from 'react'

import {sendIssueFeedback} from '../components/IssueFeedbackDialog'
import type {ActivityTimelineOptOutFeedback} from '../components/timeline/ActivityTimelinePreviewMenu'

const FEATURE_PREVIEW_SLUG = 'issue_activity_timeline_preview'
const OPT_OUT_FEEDBACK_SUBJECT = 'issue-activity-timeline-opt-out'

export async function saveActivityTimelinePreviewChoice(
  enabled: boolean,
  feedback?: ActivityTimelineOptOutFeedback,
): Promise<void> {
  const body = new FormData()
  body.append('enable', String(enabled))

  const response = await verifiedFetch(featurePreviewTogglePath({slug: FEATURE_PREVIEW_SLUG}), {method: 'POST', body})
  if (!response.ok) {
    throw new Error(`Could not turn the Activity timeline preview ${enabled ? 'on' : 'off'}: ${response.status}`)
  }

  rememberIssueViewerActivityTimelineChoice(enabled)

  // Sent after saving, so retrying a failed toggle doesn't send it twice.
  const reason = feedback?.reason.trim() ?? ''
  if (!enabled && (feedback?.rating != null || reason)) {
    await sendIssueFeedback({subject: OPT_OUT_FEEDBACK_SUBJECT, rating: feedback?.rating ?? null, content: reason})
  }
}

/** Reloads after saving so the server picks the issue feed again. */
export function useSetActivityTimelinePreviewEnabled() {
  return useCallback(async (enabled: boolean, feedback?: ActivityTimelineOptOutFeedback) => {
    await saveActivityTimelinePreviewChoice(enabled, feedback)
    sendEvent(`issue_viewer.activity_timeline_preview.${enabled ? 'turned_on' : 'turned_off'}`)
    await reloadPage()
  }, [])
}

// Stays pending while the page reloads, so callers keep showing progress. Settles if a leave-page
// prompt keeps the page open.
function reloadPage(): Promise<void> {
  return new Promise(resolve => {
    window.addEventListener(
      'beforeunload',
      event => {
        if (event.defaultPrevented) setTimeout(resolve)
      },
      {once: true},
    )
    window.location.reload()
  })
}
